import type { EnrichedFeature } from '@/hooks/useGeoLayer';

export type GeoJsonExportSchemaField = {
  exportKey: string;
  mondayColumnId: string;
  title: string;
  mondayType: string;
  source: 'registry' | 'dynamic';
};

export type GeoJsonExportSchemaManifest = {
  exportSchemaVersion: string;
  boardId: string;
  fetchedAt: string;
  fields: GeoJsonExportSchemaField[];
};

/** Always refetch Monday-backed layer features (no silent stale fallback). */
export async function fetchFreshGeoLayerFeatures(layerId: string): Promise<EnrichedFeature[]> {
  const res = await fetch(
    `/api/geo-layers/${encodeURIComponent(layerId)}/features?t=${Date.now()}`,
    { cache: 'no-store', headers: { Pragma: 'no-cache' } },
  );
  if (!res.ok) {
    throw new Error(`שגיאה בטעינת נתונים מהשרת (${res.status})`);
  }
  return (await res.json()) as EnrichedFeature[];
}

export async function fetchGeoLayerExportSchema(
  layerId: string,
): Promise<GeoJsonExportSchemaManifest | null> {
  const res = await fetch(
    `/api/geo-layers/${encodeURIComponent(layerId)}/export-schema?t=${Date.now()}`,
    { cache: 'no-store', headers: { Pragma: 'no-cache' } },
  );
  if (!res.ok) {
    return null;
  }
  return (await res.json()) as GeoJsonExportSchemaManifest;
}

export type GeoJsonFeatureCollection = {
  type: 'FeatureCollection';
  features: GeoJsonFeature[];
  /** Optional collection-level metadata (QGIS / ArcGIS Pro preserve as document info). */
  properties?: Record<string, string | number>;
};

export type GeoJsonFeature = {
  type: 'Feature';
  id?: string | number;
  geometry: GeoJsonGeometry;
  properties: Record<string, string | number | boolean | null>;
};

export type GeoJsonGeometry =
  | { type: 'Point'; coordinates: [number, number] }
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export type GeoJsonExportStats = {
  featureCount: number;
  pointCount: number;
  polygonCount: number;
  multiPolygonCount: number;
  skippedDrawnWithoutPolygon: number;
};

const CRM_STATUS_LABELS: Record<string, string> = {
  active: 'פעיל',
  lead: 'ליד',
  inactive: 'לא פעיל',
};

/** Property keys stripped from GeoJSON export (geometry blobs, file columns). */
export const GEOJSON_EXPORT_PROPERTY_DENYLIST = new Set([
  'parcel_polygon',
  'parcel_polygon_raw',
  'parcel_geometry',
  'parcel_map_feature',
  'text_mm4j9p64',
  'filezgshkkm2',
  'filev6ow2t9a',
  'filemk54lt2a',
  'file4khnuexa',
  'fileki8tq148',
  'file_mm4m76fa',
  'file_mm4mw3k7',
  'file9djsb71e',
  'signaturecrvya9ht',
  'miluim_file',
  'rmi_lease_file',
  'village_lease_file',
  'five_year_agreement_file',
  'building_permit_exemption_file',
  'existing_building_permit_file',
  'existing_project_plans_file',
  'visual_appendix_file',
  'signature',
]);

function serializePropertyValue(value: unknown): string | number | boolean | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return JSON.stringify(value);
}

function normalizeGeoJsonType(type: unknown): string {
  return typeof type === 'string' ? type.trim().toLowerCase() : '';
}

function isPolygonRing(coords: unknown): coords is number[][] {
  return (
    Array.isArray(coords) &&
    coords.length >= 3 &&
    coords.every(
      (pos) =>
        Array.isArray(pos) &&
        pos.length >= 2 &&
        Number.isFinite(pos[0]) &&
        Number.isFinite(pos[1]),
    )
  );
}

function isPolygonCoordinates(coords: unknown): coords is number[][][] {
  return Array.isArray(coords) && coords.length > 0 && coords.every(isPolygonRing);
}

function isMultiPolygonCoordinates(coords: unknown): coords is number[][][][] {
  return Array.isArray(coords) && coords.length > 0 && coords.every(isPolygonCoordinates);
}

/** Parse polygon / multi-polygon from map geometry, Monday v1 envelope, or GeoJSON Feature wrapper. */
export function resolvePolygonGeometry(
  source: unknown,
): GeoJsonGeometry | null {
  if (!source) return null;

  let value: unknown = source;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  if (!value || typeof value !== 'object') return null;
  const obj = value as Record<string, unknown>;
  const type = normalizeGeoJsonType(obj.type);

  if (type === 'feature' && obj.geometry) {
    return resolvePolygonGeometry(obj.geometry);
  }

  // Monday intake v1 parcel envelope: { v, selection_mode, migrash, geometry: Polygon }
  if (obj.geometry && typeof obj.geometry === 'object') {
    const fromNested = resolvePolygonGeometry(obj.geometry);
    if (fromNested) return fromNested;
  }

  if (type === 'polygon' && isPolygonCoordinates(obj.coordinates)) {
    return { type: 'Polygon', coordinates: obj.coordinates };
  }

  if (type === 'multipolygon' && isMultiPolygonCoordinates(obj.coordinates)) {
    return { type: 'MultiPolygon', coordinates: obj.coordinates };
  }

  return null;
}

function buildProperties(feature: EnrichedFeature): Record<string, string | number | boolean | null> {
  const props: Record<string, string | number | boolean | null> = {
    name: feature.name,
    layer_id: feature.layerId,
    layer_name: feature.layerDisplayName,
    crm_manager: feature.crm.manager,
    crm_phone: feature.crm.phone,
    crm_email: feature.crm.email,
    crm_status: CRM_STATUS_LABELS[feature.crm.status] ?? feature.crm.status,
    crm_rooms: feature.crm.rooms,
    crm_stars: feature.crm.stars,
    crm_notes: feature.crm.notes,
  };

  for (const [key, value] of Object.entries(feature.attributes)) {
    if (key in props) continue;
    if (GEOJSON_EXPORT_PROPERTY_DENYLIST.has(key)) continue;
    if (key.startsWith('file') || key.endsWith('_file')) continue;
    props[key] = serializePropertyValue(value);
  }

  return props;
}

function isPolygonDrawnSelection(feature: EnrichedFeature): boolean {
  return feature.attributes.selection_mode === 'polygon_drawn';
}

function polygonSourcesFromFeature(feature: EnrichedFeature): unknown[] {
  const g = feature.geometry;
  const attrs = feature.attributes;
  return [
    g?.shape,
    attrs.parcel_polygon,
    attrs.parcel_geometry,
    attrs.parcel_map_feature,
  ];
}

function geometryFromFeature(
  feature: EnrichedFeature,
): { geometry: GeoJsonGeometry | null; skippedDrawnWithoutPolygon: boolean } {
  const g = feature.geometry;
  if (!g) {
    return { geometry: null, skippedDrawnWithoutPolygon: false };
  }

  for (const source of polygonSourcesFromFeature(feature)) {
    const polygon = resolvePolygonGeometry(source);
    if (polygon) {
      return { geometry: polygon, skippedDrawnWithoutPolygon: false };
    }
  }

  if (isPolygonDrawnSelection(feature)) {
    return { geometry: null, skippedDrawnWithoutPolygon: true };
  }

  if (Number.isFinite(g.lat) && Number.isFinite(g.lng)) {
    return {
      geometry: { type: 'Point', coordinates: [g.lng, g.lat] },
      skippedDrawnWithoutPolygon: false,
    };
  }

  return { geometry: null, skippedDrawnWithoutPolygon: false };
}

/** Builds a GeoJSON FeatureCollection from map features (points and polygons). */
export function enrichedFeaturesToFeatureCollection(
  features: EnrichedFeature[],
  meta?: {
    layerId: string;
    layerDisplayName: string;
    exportedAt?: string;
    mondayBoardId?: string;
    exportSchemaVersion?: string;
    exportSchema?: GeoJsonExportSchemaManifest | null;
  },
): GeoJsonFeatureCollection & { stats: GeoJsonExportStats } {
  const exportedAt = meta?.exportedAt ?? new Date().toISOString();
  const exportSchemaVersion =
    meta?.exportSchemaVersion ??
    meta?.exportSchema?.exportSchemaVersion ??
    (typeof features[0]?.attributes?.export_schema_version === 'string'
      ? features[0].attributes.export_schema_version
      : undefined);
  const mondayBoardId =
    meta?.mondayBoardId ??
    meta?.exportSchema?.boardId ??
    (typeof features[0]?.attributes?.monday_board_id === 'string'
      ? features[0].attributes.monday_board_id
      : undefined);
  const schemaFieldCatalog = meta?.exportSchema?.fields?.map((f) => f.exportKey) ?? [];
  const fieldCatalog = [
    ...new Set([...schemaFieldCatalog, ...collectExportFieldCatalog(features)]),
  ].sort();

  const stats: GeoJsonExportStats = {
    featureCount: 0,
    pointCount: 0,
    polygonCount: 0,
    multiPolygonCount: 0,
    skippedDrawnWithoutPolygon: 0,
  };

  const collection: GeoJsonFeatureCollection = {
    type: 'FeatureCollection',
    features: [],
    properties: {
      generator: 'MondayGIS',
      format: 'GeoJSON RFC 7946',
      crs: 'EPSG:4326',
      exported_at: exportedAt,
      ...(meta
        ? {
            layer_id: meta.layerId,
            layer_name: meta.layerDisplayName,
            feature_count: 0,
            point_count: 0,
            polygon_count: 0,
            ...(exportSchemaVersion ? { export_schema_version: exportSchemaVersion } : {}),
            ...(mondayBoardId ? { monday_board_id: mondayBoardId } : {}),
            ...(meta.exportSchema?.fetchedAt
              ? { export_schema_fetched_at: meta.exportSchema.fetchedAt }
              : {}),
            ...(fieldCatalog.length > 0 ? { field_catalog: fieldCatalog.join(',') } : {}),
          }
        : {}),
    },
  };

  for (const feature of features) {
    const { geometry, skippedDrawnWithoutPolygon } = geometryFromFeature(feature);
    if (skippedDrawnWithoutPolygon) {
      stats.skippedDrawnWithoutPolygon += 1;
      continue;
    }
    if (!geometry) continue;

    if (geometry.type === 'Point') stats.pointCount += 1;
    if (geometry.type === 'Polygon') stats.polygonCount += 1;
    if (geometry.type === 'MultiPolygon') stats.multiPolygonCount += 1;

    collection.features.push({
      type: 'Feature',
      id: feature.id,
      geometry,
      properties: {
        ...buildProperties(feature),
        export_geometry_type: geometry.type,
      },
    });
  }

  stats.featureCount = collection.features.length;
  if (collection.properties) {
    collection.properties.feature_count = stats.featureCount;
    collection.properties.point_count = stats.pointCount;
    collection.properties.polygon_count =
      stats.polygonCount + stats.multiPolygonCount;
  }

  return { ...collection, stats };
}

function collectExportFieldCatalog(features: EnrichedFeature[]): string[] {
  const keys = new Set<string>();
  for (const feature of features) {
    for (const key of Object.keys(feature.attributes)) {
      if (GEOJSON_EXPORT_PROPERTY_DENYLIST.has(key)) continue;
      if (key.startsWith('file') || key.endsWith('_file')) continue;
      keys.add(key);
    }
  }
  return [...keys].sort();
}

export type GeoJsonLayerKind = 'all' | 'parcels' | 'sites';

export function buildGeoJsonFilename(
  layerId: string,
  exportedAt = new Date(),
  kind: GeoJsonLayerKind = 'all',
): string {
  const stamp = exportedAt.toISOString().slice(0, 10);
  const safeLayer = layerId.replace(/[^a-z0-9_-]+/gi, '-');
  const suffix =
    kind === 'parcels' ? '-parcels' : kind === 'sites' ? '-sites' : '';
  return `monday-gis-${safeLayer}${suffix}-${stamp}.geojson`;
}

function isAreaGeometry(geometry: GeoJsonGeometry): boolean {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

/** Split export into homogeneous collections (required by ArcGIS Online GeoJSON layers). */
export function splitFeatureCollectionsForArcGis(
  features: EnrichedFeature[],
  meta?: {
    layerId: string;
    layerDisplayName: string;
    exportedAt?: string;
    exportSchema?: GeoJsonExportSchemaManifest | null;
  },
): {
  parcels: GeoJsonFeatureCollection | null;
  sites: GeoJsonFeatureCollection | null;
  stats: GeoJsonExportStats;
} {
  const combined = enrichedFeaturesToFeatureCollection(features, meta);
  const baseProps = combined.properties ?? {};

  const parcelFeatures = combined.features.filter((f) => isAreaGeometry(f.geometry));
  const siteFeatures = combined.features.filter((f) => f.geometry.type === 'Point');

  const parcels =
    parcelFeatures.length > 0
      ? {
          type: 'FeatureCollection' as const,
          features: parcelFeatures,
          properties: {
            ...baseProps,
            export_layer_kind: 'parcels',
            geometry_type: 'Polygon',
            feature_count: parcelFeatures.length,
            arcgis_note:
              'ArcGIS Online: add this file as a polygon layer (not the -sites file).',
          },
        }
      : null;

  const sites =
    siteFeatures.length > 0
      ? {
          type: 'FeatureCollection' as const,
          features: siteFeatures,
          properties: {
            ...baseProps,
            export_layer_kind: 'sites',
            geometry_type: 'Point',
            feature_count: siteFeatures.length,
            arcgis_note:
              'ArcGIS Online: add this file as a point layer (not the -parcels file).',
          },
        }
      : null;

  return { parcels, sites, stats: combined.stats };
}

/** Triggers a browser download of a `.geojson` file (QGIS, ArcGIS Pro, MapInfo, etc.). */
export function downloadGeoJsonFile(filename: string, collection: GeoJsonFeatureCollection): void {
  const json = `${JSON.stringify(collection, null, 2)}\n`;
  const blob = new Blob([json], { type: 'application/geo+json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.endsWith('.geojson') ? filename : `${filename}.geojson`;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Features that have a point or polygon geometry and can be written to GeoJSON. */
export function countExportableFeatures(features: EnrichedFeature[]): number {
  return enrichedFeaturesToFeatureCollection(features).stats.featureCount;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export type GeoJsonExportResult = {
  filenames: string[];
  stats: GeoJsonExportStats;
};

/**
 * Downloads GeoJSON for GIS apps. ArcGIS Online only reads one geometry type per layer
 * (defaults to the first feature), so polygon and point features are exported as
 * separate files when both exist.
 */
export async function exportFeaturesToGeoJsonFile(
  features: EnrichedFeature[],
  meta: {
    layerId: string;
    layerDisplayName: string;
    exportSchema?: GeoJsonExportSchemaManifest | null;
  },
): Promise<GeoJsonExportResult> {
  const exportedAt = new Date();
  const exportSchema =
    meta.exportSchema ??
    (meta.layerId === 'agritour-entrepreneurs'
      ? await fetchGeoLayerExportSchema(meta.layerId)
      : null);
  const enrichedMeta = {
    ...meta,
    exportSchema,
    exportedAt: exportedAt.toISOString(),
  };
  const { parcels, sites, stats } = splitFeatureCollectionsForArcGis(features, enrichedMeta);

  const filenames: string[] = [];

  if (parcels) {
    const parcelsName = buildGeoJsonFilename(meta.layerId, exportedAt, 'parcels');
    downloadGeoJsonFile(parcelsName, parcels);
    filenames.push(parcelsName);
  }

  if (sites) {
    if (filenames.length > 0) {
      await delay(400);
    }
    const sitesName = buildGeoJsonFilename(meta.layerId, exportedAt, 'sites');
    downloadGeoJsonFile(sitesName, sites);
    filenames.push(sitesName);
  }

  if (filenames.length === 0) {
    const { stats: emptyStats, ...collection } = enrichedFeaturesToFeatureCollection(
      features,
      enrichedMeta,
    );
    const fallbackName = buildGeoJsonFilename(meta.layerId, exportedAt);
    downloadGeoJsonFile(fallbackName, collection);
    filenames.push(fallbackName);
    return { filenames, stats: emptyStats };
  }

  return { filenames, stats };
}
